import { useMutation } from '@tanstack/react-query';
import { Auth, Cache } from 'aws-amplify';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '@/api';
import { API_ERROR_MESSAGES, API_STATUS_CODE } from '@/constants/api.constants';
import { REMEMBER_ME_INFO_KEY } from '@/constants/auth.constants';
import { END_POINT } from '@/constants/end-point.constants';
import { ROUTES_PATH } from '@/constants/routes.constants';
import {
  checkCanAccessByRole,
  getUserInformation,
} from '@/helpers/auth.helper';
import { useModalMethod } from '@/hooks/use-modal-method';
import { useCommonStore } from '@/store/common.store';

import type {
  LoginFormValues,
  PasswordRegistrationRequest,
  PasswordResetRequest,
  PasswordResetSendEmailRequest,
  PasswordVerifyRequest,
} from '../types';

const login = async (values: LoginFormValues) => {
  const { username, password } = values;
  await Auth.signIn(username, password);
  await api.get(END_POINT.organizationsOfMe, { selfHandleError: true });
};

export const useLogin = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { error: modalError } = useModalMethod();
  const [searchParams] = useSearchParams();
  const setMyAccountName = useCommonStore(state => state.setMyAccountName);

  return useMutation({
    mutationFn: (values: LoginFormValues) => login(values),
    onSuccess: async (_, variables) => {
      const canAccessByRole = checkCanAccessByRole();
      if (canAccessByRole) {
        const { is_remember_me: isRememberMe } = variables;
        const expiration = new Date(3025, 1, 1);
        Cache.setItem(REMEMBER_ME_INFO_KEY, isRememberMe ? variables : {}, {
          priority: 1,
          expires: expiration.getTime(),
        });
        const { name } = getUserInformation();
        setMyAccountName(name!);
        const redirectRoute = searchParams.has('redirect_url')
          ? `/${search.replace('?redirect_url=/', '')}`
          : ROUTES_PATH.root;
        navigate(redirectRoute, { state: { isFromLogin: true } });
        return;
      }
      await Auth.signOut();
      modalError({ content: API_ERROR_MESSAGES[API_STATUS_CODE.unauthorized] });
    },
    onError: async error => {
      if (
        error instanceof AxiosError &&
        [API_STATUS_CODE.forbidden, API_STATUS_CODE.unauthorized].includes(
          error?.response?.status as (typeof API_STATUS_CODE)[
            | 'forbidden'
            | 'unauthorized'],
        )
      ) {
        await Auth.signOut();
      }
    },
  });
};

const registerPassword = (requestBody: PasswordRegistrationRequest) =>
  api.post(END_POINT.accountsPasswordRegistration, requestBody, {
    selfHandleError: true,
  });

export const useRegisterPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: registerPassword,
    onSuccess() {
      navigate(ROUTES_PATH.passwordRegistrationSuccess);
    },
    onError(error) {
      if (error instanceof AxiosError) {
        const errorMessage = error?.response?.data?.message;
        navigate(
          errorMessage === '会員登録完了URLの有効期限が切れています。'
            ? ROUTES_PATH.passwordRegistrationExpiredError
            : ROUTES_PATH.passwordRegistrationError,
        );
      }
    },
  });
};

const resetPasswordSendEmail = (requestBody: PasswordResetSendEmailRequest) =>
  api.post(END_POINT.accountsForgotPassword, requestBody, {
    selfHandleError: true,
  });

export const useResetPasswordSendEmail = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: resetPasswordSendEmail,
    onSuccess() {
      navigate(ROUTES_PATH.passwordResetSendEmailSuccess);
    },
  });
};

const registerPasswordVerify = (requestBody: PasswordVerifyRequest) =>
  api.post(END_POINT.accountsPasswordRegistrationVerify, requestBody, {
    selfHandleError: true,
  });

export const useRegisterPasswordVerify = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: registerPasswordVerify,
    onError(error) {
      if (error instanceof AxiosError) {
        const errorMessage = error?.response?.data?.message;
        navigate(
          errorMessage === '会員登録完了URLの有効期限が切れています。'
            ? ROUTES_PATH.passwordRegistrationExpiredError
            : errorMessage === 'メールアドレスが既に登録されています。'
              ? ROUTES_PATH.passwordRegistrationEmailError
              : ROUTES_PATH.passwordRegistrationError,
        );
      }
    },
  });
};

const resetPassword = (requestBody: PasswordResetRequest) =>
  api.post(END_POINT.accountsResetPassword, requestBody, {
    selfHandleError: true,
  });

export const useResetPassword = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: resetPassword,
    onSuccess() {
      navigate(ROUTES_PATH.passwordResetSuccess);
    },
    onError(error) {
      if (error instanceof AxiosError) {
        const errorMessage = error?.response?.data?.message;
        navigate(
          errorMessage === 'パスワード再設定URLの有効期限が切れています。'
            ? ROUTES_PATH.passwordResetExpiredError
            : ROUTES_PATH.passwordResetError,
        );
      }
    },
  });
};

const resetPasswordVerify = (requestBody: PasswordVerifyRequest) =>
  api.post(END_POINT.accountsResetPasswordVerify, requestBody, {
    selfHandleError: true,
  });

export const useResetPasswordVerify = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: resetPasswordVerify,
    onError(error) {
      if (error instanceof AxiosError) {
        const errorMessage = error?.response?.data?.message;
        navigate(
          errorMessage === 'パスワード再設定URLの有効期限が切れています。'
            ? ROUTES_PATH.passwordResetExpiredError
            : ROUTES_PATH.passwordResetError,
        );
      }
    },
  });
};
